import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, AlertTriangle, ShieldOff, ListChecks } from "lucide-react";

const GROUP_LABELS = {
  business: "Business",
  operations: "Operations",
  finance: "Finance",
  platform: "Platform",
  other: "Other",
};

export default function ResetData() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAllowed = user?.role === "managing_director" || user?.role === "super_admin";
  const [confirmText, setConfirmText] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState({});

  const { data: summary = {} } = useQuery({
    queryKey: ["data-summary"],
    queryFn: async () => (await api.get("/admin/data-summary")).data,
    enabled: isAllowed,
  });
  const { data: registry = [] } = useQuery({
    queryKey: ["admin-collections"],
    queryFn: async () => (await api.get("/admin/collections")).data,
    enabled: isAllowed,
  });

  const grouped = useMemo(() => {
    const g = {};
    for (const c of registry) {
      (g[c.group] = g[c.group] || []).push(c);
    }
    return g;
  }, [registry]);

  const selectedKeys = useMemo(() => Object.entries(selected).filter(([, v]) => v).map(([k]) => k), [selected]);
  const selectedTotal = useMemo(
    () => selectedKeys.reduce((sum, k) => sum + (summary[k] || 0), 0),
    [selectedKeys, summary],
  );

  const clearMut = useMutation({
    mutationFn: (payload) => api.post("/admin/clear-demo-data", payload),
    onSuccess: (res) => {
      toast.success(`Cleared ${res.data.total} rows across ${Object.keys(res.data.deleted).length} collections`);
      setConfirmText("");
      setSelected({});
      setDialogOpen(false);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to clear data"),
  });

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const setAll = (val) => {
    const next = {};
    for (const c of registry) next[c.key] = val;
    setSelected(next);
  };

  const canOpenDialog = selectedKeys.length > 0;
  const canConfirm = confirmText.trim() === "CLEAR";
  // Human-friendly labels for the summary dialog
  const labelMap = useMemo(() => Object.fromEntries(registry.map((c) => [c.key, c.label])), [registry]);
  const summaryRows = selectedKeys.map((k) => ({ key: k, label: labelMap[k] || k, count: summary[k] || 0 }));

  if (!isAllowed) {
    return (
      <div className="p-8 max-w-2xl" data-testid="reset-data-page">
        <div className="border border-zinc-200 rounded-lg bg-white p-8 flex items-start gap-4">
          <div className="w-10 h-10 rounded-md border border-red-200 bg-red-50 grid place-items-center shrink-0">
            <ShieldOff className="w-5 h-5 text-red-700" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-extrabold tracking-tight">Access denied</h1>
            <p className="text-sm text-zinc-600 mt-2">Only the Managing Director can access data reset.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl space-y-6" data-testid="reset-data-page">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md border border-red-200 grid place-items-center bg-red-50">
          <Trash2 className="w-4 h-4 text-red-700" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Reset Data</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Selectively purge business collections. Users, employees, organization and branding are preserved.</p>
        </div>
      </div>

      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <ListChecks className="w-3.5 h-3.5" /> Collections to purge
          </div>
          <div className="flex items-center gap-3 text-xs">
            <button className="text-zinc-500 hover:text-zinc-900" onClick={() => setAll(true)} data-testid="select-all">Select all</button>
            <span className="text-zinc-300">·</span>
            <button className="text-zinc-500 hover:text-zinc-900" onClick={() => setAll(false)} data-testid="select-none">Select none</button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 divide-x divide-zinc-100">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-3">{GROUP_LABELS[group] || group}</div>
              <div className="space-y-2">
                {items.map((c) => {
                  const count = summary[c.key] ?? 0;
                  return (
                    <label
                      key={c.key}
                      className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-zinc-50 cursor-pointer"
                      data-testid={`row-${c.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={!!selected[c.key]}
                          onCheckedChange={() => toggle(c.key)}
                          disabled={count === 0}
                          data-testid={`chk-${c.key}`}
                        />
                        <span className={`text-sm ${count === 0 ? "text-zinc-400" : "text-zinc-800"}`}>{c.label}</span>
                      </div>
                      <span className="font-mono text-[11px] text-zinc-500" data-testid={`count-${c.key}`}>{count}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border border-red-200 rounded-lg bg-red-50/50">
        <div className="px-5 py-3 border-b border-red-100 flex items-center gap-2 text-red-900 font-semibold text-sm">
          <AlertTriangle className="w-4 h-4" /> Danger Zone
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="text-sm text-zinc-800">
            <div>You are about to purge <span className="font-mono font-bold text-red-700" data-testid="target-total">{selectedTotal}</span> rows across <span className="font-mono font-bold" data-testid="target-count">{selectedKeys.length}</span> collections: <span className="text-zinc-600 font-mono text-[11px]">{selectedKeys.join(", ") || "none selected"}</span></div>
            <div className="mt-2 text-zinc-600">This cannot be undone. Consider taking a backup first.</div>
          </div>
          <Button
            variant="destructive"
            disabled={!canOpenDialog || clearMut.isPending}
            onClick={() => { setConfirmText(""); setDialogOpen(true); }}
            data-testid="reset-open-dialog-btn"
          >
            {clearMut.isPending ? "Clearing…" : `Review & Clear ${selectedKeys.length} collection${selectedKeys.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </section>

      {/* Final Confirmation Dialog */}
      <AlertDialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setConfirmText(""); }}>
        <AlertDialogContent className="max-w-lg" data-testid="reset-summary-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" /> Final Confirmation — Reset Business Data
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-zinc-800">
                <div className="text-sm">
                  The following collections will be <span className="font-bold text-red-700">permanently deleted</span>.
                  Users, employees, branding, organization settings and platform configuration will be preserved.
                </div>
                <div className="border border-red-200 rounded-md bg-white overflow-hidden" data-testid="reset-summary-list">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 text-[10px] uppercase tracking-widest text-red-800">
                      <tr>
                        <th className="text-left px-4 py-2">Collection</th>
                        <th className="text-right px-4 py-2">Rows to delete</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {summaryRows.map((r) => (
                        <tr key={r.key} data-testid={`dialog-row-${r.key}`}>
                          <td className="px-4 py-1.5">{r.label}</td>
                          <td className={`px-4 py-1.5 text-right font-mono ${r.count > 0 ? "text-red-700 font-bold" : "text-zinc-400"}`}>{r.count}</td>
                        </tr>
                      ))}
                      <tr className="bg-red-50">
                        <td className="px-4 py-2 font-semibold text-red-800">Total</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-red-700" data-testid="dialog-total">{selectedTotal}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <Label htmlFor="dialog-confirm" className="text-zinc-800">
                    Type <span className="font-mono font-bold text-red-700">CLEAR</span> to confirm final deletion
                  </Label>
                  <Input
                    id="dialog-confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                    className="mt-1 h-9 font-mono w-40"
                    placeholder="CLEAR"
                    autoFocus
                    data-testid="dialog-confirm-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canConfirm || clearMut.isPending}
              onClick={() => clearMut.mutate({ confirm: "CLEAR", collections: selectedKeys })}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-300"
              data-testid="dialog-confirm-btn"
            >
              {clearMut.isPending ? "Clearing…" : `Yes, delete ${selectedTotal} rows`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
